import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 40000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("bsg_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function fcfa(amount) {
  const n = Math.round(Number(amount) || 0);
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

export function apiError(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
  const status = e?.response?.status;
  if (!e?.response || [502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530].includes(status)) {
    return "Le serveur se réveille, réessayez dans un instant ⏳";
  }
  return e?.message || "Une erreur est survenue";
}

// Fetch an authenticated PDF (Bearer header) and open it — a plain <a href> can't send the token.
export async function openPdf(path, filename = "document.pdf") {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const w = window.open(url, "_blank");
  if (!w) {
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

// Fetch an authenticated image (Bearer header) and open it in a new tab.
export async function openImage(path) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

// Fetch an authenticated file (Bearer header) and download it.
export async function downloadFile(path, filename = "export.csv") {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Session morte : token qui ne résout plus un compte (autre appareil, compte introuvable, session expirée, token invalide).
    // On vide le token et on renvoie à la connexion. NB: on n'inclut PAS "Code PIN incorrect" (401 aussi) → pas de déconnexion sur mauvais PIN.
    const d = err?.response?.data?.detail;
    const SESSION_DEAD = ["autre appareil", "Utilisateur introuvable", "Session expirée", "Token invalide"];
    if (err?.response?.status === 401 && typeof d === "string" && SESSION_DEAD.some((m) => d.includes(m))) {
      localStorage.removeItem("bsg_token");
      if (!sessionStorage.getItem("bsg_kicked")) {
        sessionStorage.setItem("bsg_kicked", "1");
        window.location.href = "/auth?expired=1";
      }
      return Promise.reject(err);
    }
    // Réessai auto pour les échecs TRANSITOIRES (démarrage à froid du serveur / passerelle) :
    // pas de réponse, timeout, ou erreurs passerelle/origine Cloudflare (502/503/504/520/521/522/523/524/525/526/527/530).
    // Sûr car ces cas signifient que la requête n'a pas abouti côté application (origine injoignable / réponse vide).
    const cfg = err.config || {};
    const status = err?.response?.status;
    const transient = !err.response || err.code === "ECONNABORTED" ||
      [502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530].includes(status);
    cfg.__retry = cfg.__retry || 0;
    const method = (cfg.method || "get").toLowerCase();
    if (transient && cfg.__retry < 5 && (method === "get" || method === "post" || method === "put")) {
      cfg.__retry += 1;
      await new Promise((res) => setTimeout(res, Math.min(cfg.__retry, 4) * 800));
      return api(cfg);
    }
    return Promise.reject(err);
  }
);

export default api;
